import { useCallback, useRef, useState } from 'react';
import { supabase } from '../supabase';

// Autoguardado por fila para grillas editables.
//
// El patrón correcto para editar en una tabla, y el que hay que usar en todas las
// pantallas del admin:
//   1. La UI se actualiza al instante (optimista) → se escribe sin esperar a la red.
//   2. La escritura va DIFERIDA (default 700ms). Sin esto, un `onChange` por tecla
//      manda una request por carácter — es lo que hacía la grilla de insumos.
//   3. Los cambios de una misma fila se ACUMULAN y salen en una sola escritura, así
//      editar dos campos seguidos no dispara updates que compitan entre sí.
//   4. Cada fila expone su estado (guardando / guardado / error) para que el usuario
//      sepa que quedó, sin un botón de guardar.
//
// No hay confirmación ni botón: la edición se guarda sola. El BORRADO es otra cosa y
// sí debe confirmarse, porque es irreversible.

export type RowState = 'saving' | 'saved' | 'error';

export function useRowAutosave<T extends { id: number | string }>(
    table: string,
    setRows: React.Dispatch<React.SetStateAction<T[]>>,
    opts?: { delayMs?: number; onError?: (msg: string) => void },
) {
    const delay = opts?.delayMs ?? 700;
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const pending = useRef<Record<string, Record<string, unknown>>>({});
    const [state, setState] = useState<Record<string, RowState>>({});

    const flush = useCallback(async (id: string | number) => {
        const key = String(id);
        const body = pending.current[key];
        delete pending.current[key];
        if (!body || Object.keys(body).length === 0) return;
        setState(s => ({ ...s, [key]: 'saving' }));
        const { error } = await supabase.from(table).update(body).eq('id', id);
        if (error) {
            setState(s => ({ ...s, [key]: 'error' }));
            opts?.onError?.(error.message);
            return;
        }
        setState(s => ({ ...s, [key]: 'saved' }));
        // el ✓ se va solo: no queremos ruido permanente en la grilla
        setTimeout(() => setState(s => { const n = { ...s }; delete n[key]; return n; }), 1600);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table]);

    /** Edita un campo: pinta al instante y agenda la escritura. */
    const edit = useCallback((id: string | number, patch: Record<string, unknown>) => {
        setRows(rows => rows.map(r => (String(r.id) === String(id) ? { ...r, ...patch } : r)));
        const key = String(id);
        pending.current[key] = { ...(pending.current[key] || {}), ...patch };
        clearTimeout(timers.current[key]);
        timers.current[key] = setTimeout(() => flush(id), delay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [delay, flush]);

    /** Guarda ya, sin esperar el debounce (para selects, switches, imágenes). */
    const editNow = useCallback((id: string | number, patch: Record<string, unknown>) => {
        setRows(rows => rows.map(r => (String(r.id) === String(id) ? { ...r, ...patch } : r)));
        const key = String(id);
        pending.current[key] = { ...(pending.current[key] || {}), ...patch };
        clearTimeout(timers.current[key]);
        return flush(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flush]);

    return { edit, editNow, state };
}
